import React, { useState } from 'react';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import { useTranslation } from 'react-i18next';
import { ApiCallStatus, useAsyncData } from '../../hooks';
import { useOvermindState, useEffects } from '../../state';
import css from './BulletinsTab.module.scss';
import { Bulletin, BulletinFile } from '../../../interfaces/customer-api';
import { Spinner } from '../Spinner/Spinner';
import { Column, PaginationTable } from '../PaginationTable/PaginationTable';
import { cns, formatDate, mkEnv, toTitleCase } from '../../utils';
import { RouteComponentProps } from '@reach/router';
import { DateTime } from 'luxon';
import Search from '../../assets/search.svg';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Input } from '../Input/Input';
import { Dropdown } from '../DropDown/DropDown';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import { getLanguageName } from '../../languages';

const DEFAULT_LANGUAGE = 'EN';
const SHOW_INTERNAL_ONLY_BANNER = true;

const filterBulletin = (
    bulletin: Bulletin,
    filterValue: string,
    selectedProducts: string[],
    selectedCategories: string[],
): boolean => {
    const normalizedProduct = bulletin.product?.toLowerCase() ?? '';
    const normalizedCategory = bulletin.type?.toLowerCase() ?? '';
    const normalizedFilterValue = filterValue.toLowerCase();

    if (
        selectedProducts.length > 0 &&
        !selectedProducts.some((product) => product.toLowerCase() === normalizedProduct)
    ) {
        return false;
    }

    if (
        selectedCategories.length > 0 &&
        !selectedCategories.some((category) => category.toLowerCase() === normalizedCategory)
    ) {
        return false;
    }

    const searchValues = compact([bulletin.fileName, bulletin.product, bulletin.subject, bulletin.type]).map((value) =>
        value.toLowerCase(),
    );

    return searchValues.some((value) => value.includes(normalizedFilterValue));
};

export const BulletinsTab: React.FunctionComponent<RouteComponentProps> = () => {
    const { t } = useTranslation('BulletinsTab');
    const { api } = useEffects();
    const { selectedCustomerNumber, language } = useOvermindState();

    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [filterValue, setFilterValue] = useState('');
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedLanguages, setSelectedLanguages] = useState<Record<string, string>>({});

    const getBulletins = () => api.getBulletins(selectedCustomerNumber);
    const [bulletins, isBulletinsLoading, apiCallStatus] = useAsyncData<Bulletin[]>(
        getBulletins,
        [],
        [selectedCustomerNumber],
    );

    const isDataLoading = isBulletinsLoading;

    const products = uniq(compact(bulletins.map((bulletin) => bulletin.product)));
    const categories = uniq(compact(bulletins.map((bulletin) => bulletin.type)));

    const filteredBulletins = bulletins.filter((bulletin) =>
        filterBulletin(bulletin, filterValue, selectedProducts, selectedCategories),
    );

    const getSelectedFile = (bulletin: Bulletin): BulletinFile | undefined => {
        const selectedLanguage = selectedLanguages[bulletin.fileName]?.toLowerCase();
        const systemLanguage = language.toLowerCase();

        return (
            bulletin.files.find((file) => file.language.toLowerCase() === selectedLanguage) ??
            bulletin.files.find((file) => file.language.toLowerCase() === systemLanguage) ??
            bulletin.files.find((file) => file.language === DEFAULT_LANGUAGE) ??
            bulletin.files[0]
        );
    };

    const columns: Column<Bulletin>[] = compact([
        {
            title: t('Bulletin'),
            accessor: (bulletin: Bulletin) => {
                return bulletin.fileName;
            },
            className: cns(css.cell),
        },
        {
            title: t('Date'),
            accessor: (bulletin: Bulletin) => {
                return bulletin.date ? formatDate(DateTime.fromISO(bulletin.date)) : null;
            },
            className: cns(css.cell),
        },
        {
            title: t('Product'),
            accessor: (bulletin: Bulletin) => {
                return toTitleCase(bulletin.product || '');
            },
            className: cns(css.cell),
        },
        {
            title: t('Subject'),
            accessor: (bulletin: Bulletin) => {
                return bulletin.subject;
            },
            className: cns(css.cell),
        },
        {
            title: t('Category'),
            accessor: (bulletin: Bulletin) => {
                return toTitleCase(bulletin.type || '');
            },
            className: cns(css.cell),
        },
        {
            title: t('Language'),
            accessor: (bulletin: Bulletin) => {
                const selectedFile = getSelectedFile(bulletin);

                return selectedFile ? (
                    bulletin.files.length <= 1 ? (
                        getLanguageName(selectedFile.language) || selectedFile.language
                    ) : (
                        <Dropdown
                            className={css.languageDropdown}
                            handleChange={(language: string) =>
                                setSelectedLanguages((previous) => ({ ...previous, [bulletin.fileName]: language }))
                            }
                            label=""
                            options={bulletin.files.map((file) => ({
                                id: file.language,
                                label: getLanguageName(file.language) || file.language,
                            })).sort((a, b) => a.label.localeCompare(b.label))}
                            selected={selectedFile.language}
                        />
                    )
                ) : null;
            },
            className: cns(css.cell, css.languageColumn),
        },
        {
            title: '',
            accessor: (bulletin: Bulletin) => {
                const selectedFile = getSelectedFile(bulletin);

                return selectedFile ? (
                    <a
                        className={css.pdfLink}
                        href={`${mkEnv.apiBaseURL}/api/v1/customers/${selectedCustomerNumber}/bulletins/${selectedFile.driveFileId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('Open pdf')}
                        <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
                    </a>
                ) : null;
            },
            className: cns(css.cell),
        },
    ]);
    return (
        <div id="bulletins-tab">
            {SHOW_INTERNAL_ONLY_BANNER && (
                <div className={css.banner}>{t('Bulletins are visible to Kalmar internal users only.')}</div>
            )}
            <div className={css.searchBarContainer}>
                <Input
                    containerProps={{
                        className: css.searchField,
                    }}
                    icon={<Search />}
                    id="bulletins-search"
                    inputProps={{
                        onChange: ({ target }) => setFilterValue(target.value),
                        placeholder: t('Search bulletins'),
                        type: 'text',
                        value: filterValue,
                        className: css.searchFieldInput,
                    }}
                />
            </div>
            <div className={css.filterContainer}>
                <MultiSelect
                    value={selectedProducts}
                    onChange={setSelectedProducts}
                    options={products.map(toTitleCase)}
                    placeholder={t('Select equipment')}
                />
                <MultiSelect
                    value={selectedCategories}
                    onChange={setSelectedCategories}
                    options={categories.map(toTitleCase)}
                    placeholder={t('Select category')}
                />
                <Dropdown
                    className={css.rowsPerPageDropdown}
                    handleChange={setRowsPerPage}
                    label={`${t('Show on page')}:`}
                    options={[
                        { id: 20, label: '20' },
                        { id: 40, label: '40' },
                        { id: 60, label: '60' },
                    ]}
                    selected={rowsPerPage}
                />
            </div>
            <div className={css.bulletinsTable}>
                {isDataLoading && (
                    <div className={css.empty}>
                        <div className={css.centered}>
                            <Spinner size={3} />
                        </div>
                    </div>
                )}
                {!isDataLoading && bulletins.length === 0 && apiCallStatus === ApiCallStatus.SUCCESS && (
                    <div className={css.empty}>
                        <p className={css.centered}>{t('No bulletins for your fleet.')}</p>
                        <p className={css.centered}>
                            {t(
                                'Bulletins are currently available for select equipment types only. We are actively expanding coverage to include the rest of your fleet in upcoming updates.',
                            )}
                        </p>
                    </div>
                )}
                {!isDataLoading && apiCallStatus === ApiCallStatus.INTERNAL_SERVER_ERROR && (
                    <div className={css.empty}>
                        <div className={css.centered}>{t('Something went wrong')}</div>
                    </div>
                )}
                {!isDataLoading && bulletins.length > 0 && (
                    <PaginationTable
                        columns={columns}
                        data={filteredBulletins}
                        id="my-bulletins-table"
                        isLoading={isDataLoading}
                        rowsPerPage={rowsPerPage}
                    />
                )}
            </div>
        </div>
    );
};